<script>
import axios             from "axios";
import Utils             from "@/js/utils";
import Swal              from "sweetalert2";
import Multiselect       from "vue-multiselect";
import AILoading         from "@/components/AILoading.vue";
import ExtensionSettings from "@/components/extension-settings";
import ExtensionUtils    from "@/js/extension";

export default {
  data() {
    return {
      loading: false,
      reply: "",
      promptParams: {}
    };
  },
  props: ["review", "permittedReply", "replyText", "generate", "sendReviewReply", "insideExtension", "responseSettings", "comments", "additionalInfo"],
  components: { Multiselect, ExtensionSettings, AILoading },
  inject: ["modals"],
  methods: {
    ...Utils.extension,
    setLoading: Utils.setLoading,
    sourceIcon: Utils.sources.iconUrl,
    async init() {
      this.reply = this.replyText || "";
      this.setManagementResponse();
    },
    setManagementResponse() {
      if (!this.reply.length) {
        const managerResponse = this.review.comments?.find(c => c.kind == "manager")?.comment || "";
        if (managerResponse.length) {
          this.reply = managerResponse;
          const obj = { name: this.$t("daily_operations.reviews.comment_type.manager"), value: managerResponse };
          this.responseSettings.saved_responses.value = obj;
          this.responseSettings.saved_responses.options.push(obj)
        }
      }
    },
    async sendReplyFromExtension() {
      this.setLoading(true);
      try {
        const response = await axios.post(`/v3/companies/${this.review.company.id}/reviews/${this.review.id}/extension_reply`, { message: this.reply });
        const comment = response.data.data?.comments?.find(c => c.type == "manager");
        this.$emit("updateReply", { comment });
        this.success();
      } catch (err) { this.error(err) }
      this.setLoading(false);
    },
    async sendReply() {
      this.setLoading(true);
      try {
        let response = await axios.post(`/v3/companies/${this.review.company.id}/reviews/${this.review.id}/reply`, { message: this.reply  });
        this.$emit("updateReply", response.data.data);
        this.success();
      } catch (err) { this.error(err) }
      this.modals.reply.review = false;
      this.setLoading(false);
    },
    async generateResponse() {
      this.notifyExtension({ type: "OLY_CONTENT_SET_LOADING", data: { loading: true, review: this.review, scroll: true } });
      this.setLoading(true);
      try {
        this.promptParams = await this.buildParamsGenerateResponse();
        const response = await axios.post("/v3/ai/generate_response", this.promptParams);
        this.reply = response.data.data.output.response;
      } catch (err) { this.error(err) }
      if (this.insideExtension) this.fillResponse();
      this.setLoading(false);
    },
    async buildParamsGenerateResponse() {
      const response = await axios.get("/v3/ai/response_prompt");
      const langCode = this.responseSettings.language.use_review_language ? this.review.language : this.responseSettings.language.customLanguage;
      return {
        prompt: [response.data],
        review_id: this.review.id,
        tonality: this.responseSettings.tonality || this.responseSettings.customTonality,
        friendly_language: this.$t(`locales.${langCode}`, "en"),
        number_words: this.responseSettings.limit_words.enabled ? this.responseSettings.limit_words.value : "No limit",
        ...(this.responseSettings.mention_property_name?.enabled && { hotel_name: this.responseSettings.mention_property_name.value }),
        ...(this.responseSettings.sign_name?.enabled && { sender_signature: this.responseSettings.sign_name.value }),
        ...(this.responseSettings.words_to_mention?.enabled && { words_to_mention: this.responseSettings.words_to_mention.selected }),
      };
    },
    addResponseTemplate() {
      let id = 1;
      if (this.responseSettings.saved_responses.options.length)
        id = Math.max(...this.responseSettings.saved_responses.options.map(o => o.id || 0)) + 1;
      const obj = { id, name: this.$t(`daily_operations.reviews.template_number`, { n: id }), value: this.reply };
      this.responseSettings.saved_responses.value = obj;
      this.updateSavedTemplateArea();
      this.responseSettings.saved_responses.options = [...this.responseSettings.saved_responses.options, this.responseSettings.saved_responses.value];
      this.updateSavedResponses();
    },
    updateTemplate() {
      const id = this.responseSettings.saved_responses.value?.id;
      if (!id) return;
      this.responseSettings.saved_responses.value.value = this.reply;
      this.updateSavedTemplateArea();
      const index = this.responseSettings.saved_responses.options.findIndex(o => o.id == id);
      this.responseSettings.saved_responses.options[index] = this.responseSettings.saved_responses.value;
      this.updateSavedResponses();
    },
    async deleteResponseTemplate(ev, id) {
      ev.stopPropagation();
      this.responseSettings.saved_responses.options = this.responseSettings.saved_responses.options.filter(o => o.id != id)
      this.responseSettings.saved_responses.value = {};
      this.updateSavedResponses();
    },
    async updateSavedResponses() {
      await this.updateExtensionSettings("saved_responses", this.responseSettings.saved_responses.options); // move to vuex
    },
    async updateExtensionSettings(field, value) {
      await this.$store.commit("extension/saveExtensionSettings", { data: { [field]: Utils.deepClone(value) }});
      const params = this.savedSettings;
      await axios.post("/v3/users/extension_settings", params);
    },
    updateSavedTemplateArea() {
      if (!this.responseSettings.saved_responses.value) return;
      let params = {
        words_to_mention: this.responseSettings.words_to_mention.selected,
        sender_signature: this.responseSettings.sign_name.value,
        hotel_name:       this.responseSettings.mention_property_name.value
      };
      params = { ...params, reviewer_name: this.review.reviewer_name };
      let res = this.reply;

      Object.entries(params).forEach(([param, value]) => {
        if (value) res = res.replace(value, `%{${param}}`);
      });

      this.responseSettings.saved_responses.value.value = res;
    },
    updateResponseArea() {
      let params = {
        words_to_mention: this.responseSettings.words_to_mention.selected,
        sender_signature: this.responseSettings.sign_name.value,
        hotel_name:       this.responseSettings.mention_property_name.value
      };
      params = { ...params, reviewer_name: this.review.reviewer_name };
      let res = this.responseSettings.saved_responses.value?.value || "";

      Object.entries(params).forEach(([param, value]) => {
        res = res.replace(`%{${param}}`, value);
      });

      this.reply = res;
    },
    handleReplyAction() {
      if (this.extensionIntegrated) this.fillResponse();
      else this.replyCopy();
    },
    handleReply() {
      if (this.insideExtension) return this.fillResponse();
      this.extensionToOTA();
    },
    replyCopy() {
      navigator.clipboard.writeText(this.reply);
    },
    fillResponse() {
      this.notifyExtension({ type: "OLY_CONTENT_FILL_RESPONSE", data: { review: this.review, response: this.reply, scroll: true } });
    },
    extensionToOTA() {
      this.notifyExtension({
        type: "OLY_CONTENT_OPEN_OTA_WITH_SIDE_PANEL",
        data: { review: this.review, response: this.reply, url: this.review.respond_url, params: { ...this.$route.query, text: this.reply, rm: true, rid: this.review.id, source: this.review.source } }
      });
    },
    toggleSettingsModal(ev) {
      ev?.preventDefault();
      this.modals.extension_settings = !this.modals.extension_settings;
    },
    regenerateResponse() {
      this.generateResponse();
      this.toggleSettingsModal();
    },
    success() {
			Swal.fire(this.$t("general.success"), this.$t("daily_operations.reviews.reply_sent_success"), "success");
		},
    error(error) {
      return Swal.fire(this.$t("general.error"), this.$t("general.contact_support") + JSON.stringify(error.response?.data), "error");
    }
  },
  computed: {
    canReplyLocally() {
      return Utils.permittedReviewSources.includes(this.review?.source) && this.permittedReply;
    },
    savedSettings() {
      return this.$store.getters["extension/extensionSettings"];
    },
    isDirtySavedResponse() {
      const prev = this.savedSettings?.saved_responses?.find(sr => sr.id == this.responseSettings.saved_responses.value?.id);
      if (!prev && !this.responseSettings.saved_responses.value?.id) return false;
      return !!["name", "value"].reduce((res, k) => {
        if (this.responseSettings.saved_responses.value[k] != prev?.[k]) return res + 1;
        return res;
      }, 0);
    },
    extensionInstalled() {
      return this.getExtensionInstalled();
    },
    extensionIntegrated() {
      if (!this.extensionInstalled) return false;
      const list = Object.keys(ExtensionUtils);
      return list.includes(this.review.source);
    },
    replyLabel() {
      if (this.extensionIntegrated) return this.$t("daily_operations.reviews.reply_in_ota", { source: this.review.source_name });
      return this.$t("daily_operations.reviews.copy_and_go", { source: this.review.source_name });
    },
    reviewerName() {
      return this.review.reviewer?.name || this.$t("surveys_responses.no_name");
    },
  },
  watch: {
    review: {
      handler: function () {
        if (this.review?.id) this.init();
      },
      immediate: true,
      deep: true
    },
    generate: {
      handler: function () {
        if (this.insideExtension) {
          if (this.generate) this.generateResponse();
          else this.fillResponse();
        }
      },
      immediate: true
    },
    sendReviewReply: {
      handler: function () {
        if (this.insideExtension && this.sendReviewReply) {
          this.setManagementResponse();
          this.sendReplyFromExtension();
        }
      },
      immediate: true
    },
    replyText: {
      handler: function () {
        this.reply = this.replyText || "";
      },
      immediate: true
    }
  }
};
</script>

<template>
  <div class="review-reply" v-if="review && review.id">
    <div class="h-100">
      <div class="row pb-2">
        <!-- review -->
        <div class="col-12 col-sm-5 pe-sm-2 d-flex flex-column">
          <div class="mb-2 d-flex align-items-center">
            <h5 class="m-0">{{ $t("daily_operations.reviews.review") }}</h5>
            <div class="hide">
              <button class="btn btn-primary ps-2">
                <i class="mdi mdi-creation me-1"></i>
                {{ $t("daily_operations.reviews.generate_response") }}
              </button>
              <BButtonGroup>
                <BButton class="py-1" variant="outline-primary">
                  <i class="mdi mdi-content-save-plus font-size-16"></i>
                </BButton>
              </BButtonGroup>
            </div>
          </div>

          <div class="position-relative rounded-3 overflow-auto border border-2 flex-grow-1" :style="insideExtension && 'min-height: 400px;'">
            <div class="position-absolute p-2 w-100">
              <div class="mb-3 d-flex flex-column">
                <div class="d-flex align-items-center w-100">
                  <img v-if="review.source" class="rounded me-3 ratio-1x1" :src=sourceIcon(review.source) style="width:40px;">
                  <div>
                    <h5 class="mb-0">{{ review.title }}</h5>
                    <div class="d-flex flex-column">
                      <span class="mb-0 me-0 me-sm-3">{{ $t("daily_operations.reviews.by_name", { name: reviewerName }) }}</span>
                      <a v-if="review.reviewer && review.reviewer.email" :href="review.reviewer.email" target="_blank" class="text-primary">
                        {{ review.reviewer.email }}
                      </a>
                    </div>
                  </div>
                </div>

                <div class="fw-bold align-self-start mt-2">
                  {{ review.creation_date }}
                </div>
              </div>

              <!-- comments -->
              <template v-if="comments">
                <div v-for="(comment, type) in comments" :key="`review_reply_${review.id}_${type}`" class="mb-4">
                  <div class="d-flex align-items-start mb-1">
                    <h6 class="m-0">{{ $t(`daily_operations.reviews.comment_type.${type}`) }}</h6>
                    <div v-if="comment.temp" class="ms-2 d-flex align-items-center font-size-12 text-muted">
                      <i class="mdi mdi-timer-outline me-1"></i>
                      <div>{{ $t("daily_operations.reviews.wait_source", { source: review.source_name }) }}</div>
                    </div>
                  </div>
                  <span v-html="comment.text" class="white-space-pre"></span>
                </div>
              </template>

              <!-- details -->
              <div class="table-responsive mb-0">
                <h5 class="mb-1">{{ $t("daily_operations.reviews.details") }}</h5>
                <table class="w-100">
                  <template v-for="(row, index) in additionalInfo">
                    <tr v-if="review[row.field]" :key="index">
                      <td class="pb-1">
                        <label class="me-2 mb-0">{{ $t(`surveys_responses.additional_info_fields.${row.i18nPath}`) }}</label>
                      </td>
                      <td class="justify-content-start">
                        {{ review[row.field] }}
                      </td>
                    </tr>
                  </template>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- reply text -->
        <div class="col-12 col-sm-7 mt-4 mt-sm-0 ps-sm-0" :class="{ 'position-relative': loading }">
          <div class="mb-2 d-flex flex-row align-items-center justify-content-between">
            <h5 class="m-0">{{ $t("daily_operations.reviews.reply_text") }}</h5>

            <div class="d-flex align-items-center">
              <button class="btn btn-primary ps-2" @click="generateResponse" :disabled="loading">
                <i class="mdi mdi-creation me-1"></i>
                {{ $t("daily_operations.reviews.generate_response") }}
              </button>

              <BButtonGroup class="ms-2" :class="{ 'd-none': !reply.length }">
                <BButton class="py-1" variant="outline-primary" @click="addResponseTemplate" v-b-tooltip.hover :title="$t('daily_operations.reviews.save_as_template')">
                  <i class="mdi mdi-content-save-plus font-size-16"></i>
                </BButton>
                <BButton v-if="(responseSettings.saved_responses.value || {}).id" class="py-1" variant="outline-primary" @click="updateTemplate" v-b-tooltip.hover :title="$t('daily_operations.reviews.update_template')">
                  <i class="mdi mdi-update font-size-16"></i>
                </BButton>
              </BButtonGroup>

              <a v-if="insideExtension" href="#" class="ms-2" @click="toggleSettingsModal">
                <i class="mdi font-size-18 mdi-cog"></i>
              </a>
            </div>
          </div>
          <div class="w-100 position-relative">
            <textarea class="w-100 reply-textarea" v-model="reply" rows="13" :placeholder="$t('daily_operations.reviews.reply_placeholder')" :disabled="loading"></textarea>
            <button v-if="insideExtension" class="btn border border-2 rounded-pill position-absolute d-flex align-items-center py-0 px-1 font-size-12" @click="handleReplyAction" style="right:5px;top:5px;">
              <i class="mdi font-size-16 me-1" :class="{ 'mdi-arrow-right-bottom': extensionIntegrated, 'mdi-content-copy': !extensionIntegrated }"></i>
              <span v-if="extensionIntegrated">{{ $t("daily_operations.reviews.replace") }}</span>
              <span v-else>{{ $t("daily_operations.reviews.copy") }}</span>
            </button>
          </div>
          <AILoading v-if="loading" class="cursor-loading position-absolute bg-white opacity-75 w-100" style="height:calc(100% - 43px);top:43px;" />
        </div>
      </div>

      <div class="row mt-2">
        <!-- saved responses -->
        <div class="col-12 col-sm-5 pe-2">
          <div class="border border-2 rounded-3 p-2 ps-3 w-100">
            <div class="d-flex flex-sm-row align-items-center align-items-sm-center justify-content-between">
              <h5 class="mb-0 me-3">{{ $t("daily_operations.reviews.saved_responses") }}</h5>
              <div class="d-flex align-items-center justify-content-center flex-grow-1 pt-1">
                <multiselect
                  class="editable-multiselect"
                  :class="{ 'pe-0': !responseSettings.saved_responses.value }"
                  v-model="responseSettings.saved_responses.value"
                  :options="responseSettings.saved_responses.options"
                  :showLabels="false"
                  :placeholder="$t('filter.select_option')"
                  track-by="name"
                  label="name"
                >
                  <template v-slot:option="props">
                    <div class="d-flex align-items-center justify-content-between">
                      <span>
                        {{ props.option.label ? props.option.label : props.option.name }}
                      </span>
                      <a v-if="!(props.option && props.option.label)" href="javascript:void(0);" @click="e => deleteResponseTemplate(e, props.option.id)" v-b-tooltip.hover :title="$t('general.delete')">
                        <i class="mdi mdi-trash-can font-size-18"></i>
                      </a>
                    </div>
                  </template>

                  <template v-slot:selection>
                    <input v-if="responseSettings.saved_responses.value" type="text" class="form-control inner-icon-input editable-input" :placeholder="$t('filter.select_option')" v-model="responseSettings.saved_responses.value.name">
                  </template>

                </multiselect>

                <div class="d-flex align-items-center justify-content-between" v-if="(responseSettings.saved_responses.value || {}).value">
                  <a href="javascript:void(0);" class="me-1 font-size-22" @click="updateSavedResponses" v-b-tooltip.hover :title="$t('general.save')">
                    <i v-if="isDirtySavedResponse" class="mdi mdi-content-save-alert"></i>
                    <i v-else class="mdi mdi-content-save-check"></i>
                  </a>
                  <a href="javascript:void(0);" class="me-1" @click="updateResponseArea" v-b-tooltip.hover :title="$t('daily_operations.reviews.copy_to_reply')">
                    <i class="mdi mdi-file-document-arrow-right font-size-20"></i>
                  </a>
                </div>
              </div>
            </div>

            <div v-if="(responseSettings.saved_responses.value || {}).value" :class="{ 'position-relative': loading }">
              <textarea class="w-100 reply-textarea mt-1" rows="10" v-model="(responseSettings.saved_responses.value || {}).value" :placeholder="$t('daily_operations.reviews.saved_responses_placeholder')" :disabled="loading"></textarea>
              <div v-if="loading" class="w-100 h-100 cursor-loading loading position-absolute opacity-75" style="top:0;"></div>
            </div>
          </div>
        </div>

        <!-- response preferences outside extension -->
        <div v-if="!insideExtension" class="col-12 col-sm-7 mt-3 mt-sm-0 ps-sm-0">
          <div class="border border-2 rounded-3 d-flex flex-column justify-content-center" :class="{ 'h-100': !(responseSettings.saved_responses.value || {}).value }">
            <a class="card-title font-weight-bold m-0 chevron d-flex align-items-center justify-content-between w-100 px-3 py-2" style="cursor: pointer;" data-bs-toggle="collapse" :href="`#review_response_preferences_${review.id}`" aria-expanded="false" :aria-controls="`review_response_preferences_${review.id}`">
              <h5 class="m-0">{{ $t("daily_operations.reviews.response_preferences") }}</h5>
              <i class="mdi mdi-chevron-down font-size-20"></i>
            </a>
            <div class="collapse px-2 pt-3 pb-2 overflow-visible-collapse" :id="`review_response_preferences_${review.id}`">
              <ExtensionSettings :review="review" :responseSettings="responseSettings" />
            </div>
          </div>
        </div>
      </div>

      <!-- response preferences for extension -->
      <b-modal v-if="insideExtension" v-model="modals.extension_settings" centered hide-footer header-class="border-0 pb-0" body-class="pt-1" :title="$t('daily_operations.reviews.response_preferences')" size="xl">
        <div class="d-flex flex-column">
          <ExtensionSettings :review="review" :responseSettings="responseSettings" />

          <div class="footer-nav d-flex justify-content-between mt-3 mb-1">
            <button class="btn btn-primary" @click="regenerateResponse">
              <i class="mdi mdi-creation me-1"></i>
              {{ $t("daily_operations.reviews.regenerate_response") }}
            </button>

            <button class="btn btn-secondary ms-3" @click="toggleSettingsModal">
              {{ $t("general.cancel") }}
            </button>
          </div>
        </div>
      </b-modal>

      <div v-if="!insideExtension" class="float-end m-0 mt-3">
        <button v-if="canReplyLocally" class="btn btn-primary" :disabled="!reply.length" @click="sendReply">
          {{ $t("daily_operations.reviews.send_reply") }}
        </button>

        <template v-else>
          <a v-if="extensionIntegrated" @click="handleReply" class="btn btn-primary" :class="{ disabled: !reply.length }">
            {{ replyLabel }}
          </a>

          <a v-else :href="review.respond_url" target="_blank" rel="noopener noreferrer" @click="replyCopy" class="btn btn-primary" :class="{ disabled: !reply.length }">
            {{ replyLabel }}
          </a>
        </template>
      </div>
    </div>
  </div>
</template>
